import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useI18n from '../../i18n/useI18n'
import Table from '../../components/table'
import Pagination from '../../components/pagination'
import StudentsFilter from './filter'
import { BadgeCellRenderer, LogoCellRenderer } from '../../components/table-cell-renderers'
import ImageModal from '../../components/image-modal'
import useTableDataSync from '../../infrastructure/event-bus/hooks/use-table-data-sync'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import EventBus from '../../infrastructure/event-bus/event-bus'
import general from '../../utils/general'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum } from '../../utils/enums'
import AssignAgentModal from './AssignAgentModal'
import StudentDetailModal from './StudentDetailModal'
import AgentInfoModal from './AgentInfoModal'

const Students = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useI18n()
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  })

  const [filters, setFilters] = useState({
    search: '',
    city: '',
    country: '',
    nationality: '',
    agentId: ''
  })
  const [isAssignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [isDetailModalOpen, setDetailModalOpen] = useState(false)
  const [detailStudent, setDetailStudent] = useState(null)
  const [isAgentInfoModalOpen, setAgentInfoModalOpen] = useState(false)
  const [agentInfoData, setAgentInfoData] = useState(null)

  // Image Modal states
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    imageAlt: ''
  })

  const buildQueryParams = (page, filterParams, limitOverride, sortParams = null) => {
    const queryParams = {
      page,
      limit: limitOverride || pagination.limit
    }

    if (filterParams.search && filterParams.search.trim()) {
      queryParams.search = filterParams.search.trim()
    }
    if (filterParams.city && filterParams.city.trim()) {
      queryParams.city = filterParams.city.trim()
    }
    if (filterParams.country && filterParams.country.trim()) {
      queryParams.country = filterParams.country.trim()
    }
    if (filterParams.nationality && filterParams.nationality.trim()) {
      queryParams.nationality = filterParams.nationality.trim()
    }
    if (filterParams.agentId && filterParams.agentId.trim()) {
      queryParams.agentId = filterParams.agentId.trim()
    }

    // Add sort parameters if provided
    if (sortParams && sortParams.sort && sortParams.order) {
      queryParams.sort = sortParams.sort
      queryParams.order = sortParams.order
    }

    return queryParams
  }

  const fetchStudents = (page = 1, filterParams = filters, limitOverride = null, sortParams = null) => {
    setLoading(true)

    dispatch(
      ApiCallActions.Get({
        url: 'students',
        query: buildQueryParams(page, filterParams, limitOverride, sortParams),
        onSuccess: ({ data }) => {
          setStudents(data.data || [])
          setPagination({
            total: data.meta?.total || 0,
            page: data.meta?.page || 1,
            limit: data.meta?.limit || pagination.limit,
            totalPages: data.meta?.totalPages || 1
          })
          setLoading(false)
        },
        onError: () => {
          setStudents([])
          setLoading(false)
        }
      })
    )
  }

  useEffect(() => {
    fetchStudents(1, filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePageChange = (page) => {
    fetchStudents(page, filters)
  }

  const handleLimitChange = (newLimit) => {
    const updatedPagination = {
      ...pagination,
      limit: newLimit,
      page: 1
    }
    setPagination(updatedPagination)
    fetchStudents(1, filters, newLimit)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleFilterSubmit = (newFilters) => {
    setFilters(newFilters)
    fetchStudents(1, newFilters)
  }

  const handleClearFilters = (clearedFilters) => {
    setFilters(clearedFilters)
    fetchStudents(1, clearedFilters)
  }

  const handleSort = (columnKey, direction) => {
    const sortParams = direction ? { sort: columnKey, order: direction } : null
    fetchStudents(pagination.page, filters, pagination.limit, sortParams)
  }

  const handleDeleteClick = async (row) => {
    const fullName = `${row?.user?.firstName || ''} ${row?.user?.lastName || ''}`.trim() || t('STUDENTS_ENTITY')
    const confirmed = await general.confirmDelete(t('STUDENTS_ENTITY'), fullName)

    if (!confirmed) {
      return
    }

    dispatch(
      ApiCallActions.Delete({
        url: `students/${row.id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          EventBus.sc.onEntityDeleted({
            entityType: 'student',
            entityId: row.id
          })
          general.showDeleteSuccess(t('STUDENTS_ENTITY'))
        }
      })
    )
  }

  useTableDataSync(
    'student',
    students,
    setStudents,
    pagination,
    setPagination,
    (page, filterParams) => fetchStudents(page, filterParams || filters),
    filters
  )

  const resolveAgent = (row) => {
    if (!row) return null
    if (row.agent) return row.agent
    if (Array.isArray(row.agents) && row.agents.length > 0) return row.agents[0]
    return null
  }

  const getAgentNameFromAgentObject = (agent) => {
    if (!agent) return ''
    if (agent.companyName) return agent.companyName
    if (agent.name) return agent.name
    if (agent.fullName) return agent.fullName

    const firstName = agent.firstName || agent?.user?.firstName || ''
    const lastName = agent.lastName || agent?.user?.lastName || ''
    const combined = `${firstName} ${lastName}`.trim()

    if (combined) return combined

    return agent.email || agent?.user?.email || ''
  }

  const getAgentName = (row) => {
    if (!row) return ''

    const directName =
      row.agentName ||
      row.agentFullName ||
      row.assignedAgentName ||
      row.assignedAgentFullName ||
      row.agentUser?.fullName

    if (directName && typeof directName === 'string' && directName.trim()) {
      return directName.trim()
    }

    const agentUserFirst = row?.agentUser?.firstName || ''
    const agentUserLast = row?.agentUser?.lastName || ''
    const agentUserCombined = `${agentUserFirst} ${agentUserLast}`.trim()
    if (agentUserCombined) return agentUserCombined

    const agent = resolveAgent(row)
    return getAgentNameFromAgentObject(agent)
  }

  const hasAgent = (row) => {
    if (!row) return false

    const hasDirectName = Boolean(
      (typeof row.agentName === 'string' && row.agentName.trim()) ||
        (typeof row.agentFullName === 'string' && row.agentFullName.trim()) ||
        (typeof row.assignedAgentName === 'string' && row.assignedAgentName.trim()) ||
        (typeof row.assignedAgentFullName === 'string' && row.assignedAgentFullName.trim()) ||
        (typeof row?.agentUser?.fullName === 'string' && row.agentUser.fullName.trim()) ||
        (row?.agentUser &&
          `${row.agentUser.firstName || ''} ${row.agentUser.lastName || ''}`.trim())
    )

    return !!(row?.agentId || resolveAgent(row) || hasDirectName)
  }

  const handleOpenAssignModal = (row) => {
    setSelectedStudent(row)
    setAssignModalOpen(true)
  }

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false)
    setSelectedStudent(null)
  }

  const handleAssigned = () => {
    fetchStudents(pagination.page, filters, pagination.limit)
  }

  const handleOpenDetailModal = (row) => {
    setDetailStudent(row)
    setDetailModalOpen(true)
  }

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false)
    setDetailStudent(null)
  }

  const handleOpenAgentInfoModal = (row) => {
    const agent = resolveAgent(row)
    const agentName = getAgentName(row)

    if (!agent && !agentName) return

    setAgentInfoData({ agent, agentName })
    setAgentInfoModalOpen(true)
  }

  const handleCloseAgentInfoModal = () => {
    setAgentInfoModalOpen(false)
    setAgentInfoData(null)
  }

  const handleLogoClick = (imageUrl, imageAlt) => {
    if (imageUrl) {
      setImageModal({
        isOpen: true,
        imageUrl: imageUrl,
        imageAlt: imageAlt
      })
    }
  }

  const handleCloseModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: '',
      imageAlt: ''
    })
  }

  const tableColumns = [
    {
      key: 'fullName',
      label: t('STUDENTS_TABLE_COLUMNS_FULL_NAME'),
      sortable: true,
      render: (_, row) => {
        const name = `${row?.user?.firstName || ''} ${row?.user?.lastName || ''}`.trim()
        return name || '-'
      }
    },
    {
      key: 'email',
      label: t('STUDENTS_TABLE_COLUMNS_EMAIL'),
      sortable: true,
      render: (_, row) => row?.user?.email || '-'
    },
    {
      key: 'phone',
      label: t('STUDENTS_TABLE_COLUMNS_PHONE'),
      sortable: true,
      render: (_, row) => row?.user?.phone || '-'
    },
    {
      key: 'city',
      label: t('STUDENTS_TABLE_COLUMNS_CITY'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'country',
      label: t('STUDENTS_TABLE_COLUMNS_COUNTRY'),
      sortable: true,
      render: (value) => value || '-'
    },
   
    {
      key: 'agent',
      label: t('STUDENTS_TABLE_COLUMNS_AGENT'),
      sortable: true,
      render: (_, row) => {
        const agent = resolveAgent(row)
        const agentName = getAgentName(row)
        const logoUrl = agent?.logoUrl || agent?.logoURL || agent?.logo

        if (agent || agentName) {
          return (
            <div className="d-flex align-items-center">
              {logoUrl ? (
                <LogoCellRenderer
                  imageUrl={logoUrl}
                  imageAlt={agentName || t('STUDENTS_TABLE_COLUMNS_AGENT')}
                  onClick={handleLogoClick}
                  style={{ width: '26px', height: '26px', marginRight: '8px' }}
                />
              ) : null}
              <span>{agentName || t('STUDENTS_TABLE_UNASSIGNED')}</span>
              <div
                className="show-info-btn"
                onClick={() => handleOpenAgentInfoModal(row)}
                title={t('STUDENTS_AGENT_INFO_BUTTON')}
              >
                <i className="zmdi zmdi-info" ></i>
              </div>
             </div>
          )
        }

        return <span className="badge badge-info">{t('STUDENTS_TABLE_UNASSIGNED')}</span>
      }
    },
   
    {
      key: 'kvkkStatus',
      label: t('STUDENTS_TABLE_COLUMNS_KVKK'),
      sortable: true,
      render: (value) => (
        <BadgeCellRenderer
          value={value}
          options={{
            trueLabel: t('STUDENTS_TABLE_KVKK_APPROVED'),
            falseLabel: t('STUDENTS_TABLE_KVKK_MISSING'),
            trueClass: 'badge-success',
            falseClass: 'badge-warning'
          }}
        />
      )
    },
    {
      key: 'status',
      label: t('STUDENTS_TABLE_COLUMNS_STATUS'),
      sortable: true,
      render: (_, row) => (
        <BadgeCellRenderer
          value={row?.user?.status === 'active'}
          options={{
            trueLabel: t('STUDENTS_TABLE_STATUS_ACTIVE'),
            falseLabel: t('STUDENTS_TABLE_STATUS_INACTIVE'),
            trueClass: 'badge-success',
            falseClass: 'badge-danger'
          }}
        />
      )
    }
  ]

  const renderActions = (row) => (
    <div className="d-flex align-items-center">
      <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN, UserTypeEnum.AGENT]}>
        <button
          className="btn btn-sm btn-info btn-icon btn-round m-r-5"
          onClick={() => handleOpenDetailModal(row)}
          title={t('STUDENTS_ACTIONS_DETAIL')}
        >
          <i className="zmdi zmdi-eye"></i>
        </button>
      </RoleBasedAccess>
      <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
        {!hasAgent(row) && (
          <button
            className="btn btn-sm btn-primary btn-icon btn-round m-r-5"
            onClick={() => handleOpenAssignModal(row)}
            title={t('STUDENTS_ACTIONS_ASSIGN_AGENT')}
          >
            <i className="zmdi zmdi-accounts"></i>
          </button>
        )}
      </RoleBasedAccess>
      <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN, UserTypeEnum.AGENT]}>
        <button
          className="btn btn-sm btn-warning btn-icon btn-round m-r-5"
          onClick={() => navigate(`/students/${row.id}/edit`)}
          title={t('STUDENTS_ACTIONS_EDIT')}
        >
          <i className="zmdi zmdi-edit"></i>
        </button>
      </RoleBasedAccess>
      <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
        <button
          className="btn btn-sm btn-danger btn-icon btn-round"
          onClick={() => handleDeleteClick(row)}
          title={t('STUDENTS_ACTIONS_DELETE')}
        >
          <i className="zmdi zmdi-delete"></i>
        </button>
      </RoleBasedAccess>
    </div>
  )

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {t('STUDENTS_LIST_TITLE')}
              <small>{t('STUDENTS_LIST_SUBTITLE')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <RoleBasedAccess allowedRoles={[UserTypeEnum.AGENT]}>
              <button
                className="btn btn-white btn-icon btn-round float-right m-l-10"
                type="button"
                onClick={() => navigate('/students/new')}
              >
                <i className="zmdi zmdi-plus" title={t('STUDENTS_ACTIONS_NEW')}></i>
              </button>
            </RoleBasedAccess>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                </a>
              </li>
              <li className="breadcrumb-item active">{t('STUDENTS_LIST_TITLE')}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            <StudentsFilter
              filters={filters}
              onFilterChange={handleFilterChange}
              onFilterSubmit={handleFilterSubmit}
              onClearFilters={handleClearFilters}
              onRefresh={() => fetchStudents(pagination.page, filters, pagination.limit)}
              loading={loading}
            />

            <Table
              columns={tableColumns}
              data={students}
              loading={loading}
              emptyMessage={t('STUDENTS_TABLE_EMPTY')}
              renderActions={renderActions}
              onSort={handleSort}
              pagination={pagination}
            />

            <AssignAgentModal
              isOpen={isAssignModalOpen}
              student={selectedStudent}
              onClose={handleCloseAssignModal}
              onAssigned={handleAssigned}
            />

            <StudentDetailModal
              isOpen={isDetailModalOpen}
              student={detailStudent}
              onClose={handleCloseDetailModal}
              onShowAgentInfo={() => handleOpenAgentInfoModal(detailStudent)}
            />

            <AgentInfoModal
              isOpen={isAgentInfoModalOpen}
              agent={agentInfoData?.agent}
              agentName={agentInfoData?.agentName}
              onClose={handleCloseAgentInfoModal}
            />

            {/* Image Modal */}
            <ImageModal
              isOpen={imageModal.isOpen}
              imageUrl={imageModal.imageUrl}
              imageAlt={imageModal.imageAlt}
              onClose={handleCloseModal}
            />

            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default Students


